import React, { useState, useEffect, useMemo } from 'react';
import {
    Drawer,
    Space,
    Flex,
    Select,
    Input,
    Switch,
    Button,
    DatePicker,
    Divider
} from 'antd';
import type { ChangeEvent } from 'react';
import { MenuOptionDropdown } from '../../types/types';
import dayjs, { Dayjs } from 'dayjs';   
import { lookupMenuItems } from '../../services/menuservice';
import { MenuItemLookup } from '../../types/menutypes';
import debounce from 'lodash/debounce';
import { useAuth } from '../../auth/authcontext';

const { Option } = Select;

interface MenuOptionEditorDrawerProps {
    open: boolean;
    onClose: () => void;
    menuOptionsDropDown: MenuOptionDropdown[];
    selectedMenuOptionGuid: string;
    handleOptionChange: (value: string) => void;
    isWarnMenuOption: boolean;
    selectedOptionDisplayName: string;
    handleOptionDisplayTextChange: (e: ChangeEvent<HTMLInputElement>) => void;
    isWarnOptionDisplayName: boolean;
    selectedOptionDefaultSelection: boolean;
    selectedOptionStartDate: Dayjs | null;
    selectedOptionEndDate: Dayjs | null;
    handleDefaultOptionChange: (checked: boolean) => void;
    handleOptionStartDateChange: (date: Dayjs | null) => void;
    handleOptionEndDateChange: (date: Dayjs | null) => void;
    handleSaveOptionLink: () => void;
    handleCancelOptionLink: () => void;
}

const MenuOptionEditorDrawer: React.FC<MenuOptionEditorDrawerProps> = ({
    open,
    onClose,
    menuOptionsDropDown,
    selectedMenuOptionGuid,
    handleOptionChange,
    isWarnMenuOption,
    selectedOptionDisplayName,
    handleOptionDisplayTextChange,
    isWarnOptionDisplayName,
    selectedOptionDefaultSelection,
    selectedOptionStartDate,
    selectedOptionEndDate,    
    handleDefaultOptionChange,
    handleOptionStartDateChange,
    handleOptionEndDateChange,
    handleSaveOptionLink,
    handleCancelOptionLink,
}) => {
    const { isReadOnly } = useAuth();
    const [source, setSource] = useState<"menu" | "product" | null>(null);
    const [items, setItems] = useState<MenuItemLookup[]>([]);
    const [loadingItems, setLoadingItems] = useState(false);

    useEffect(() => {
        if (open) {
            if (selectedMenuOptionGuid && selectedMenuOptionGuid !== "(SELECT)") {
                const matchedOption = menuOptionsDropDown.find(o => o.key === selectedMenuOptionGuid);
                if (matchedOption) {
                    const inferredSource = matchedOption.itemType === 0 ? "product" : "menu";
                    setSource(inferredSource);
                    setItems([{
                        id: inferredSource === "product" ? matchedOption.upc : matchedOption.key,
                        menuGuid: matchedOption.key,
                        name: matchedOption.name
                    }]);
                }
            } else {
                setSource(null);
                setItems([]);
            }
        }
    }, [open, selectedMenuOptionGuid, menuOptionsDropDown]);

    const fetchLookupItems = async (query = "", selectedSource = source) => {
        if (!selectedSource) return;
        setLoadingItems(true);
        try {
            const res = await lookupMenuItems(selectedSource, query, 50);
            setItems(res.items);
        } catch (error) {
            console.error("Failed to load lookup items", error);
        } finally {
            setLoadingItems(false);
        }
    };

    const debouncedSearch = useMemo(
        () => debounce((value: string) => fetchLookupItems(value), 300),
        [source]
    );

    const handleSourceChange = (val: "menu" | "product") => {
        setSource(val);
        setItems([]);
        handleOptionChange("");
        fetchLookupItems("", val);
    };

    const handleSelectItem = (menuGuid: string) => {
        handleOptionChange(menuGuid);
        const selectedItem = items.find(i => i.menuGuid === menuGuid);
        if (selectedItem && !selectedOptionDisplayName) {
            const fakeEvent = { target: { value: selectedItem.name } } as React.ChangeEvent<HTMLInputElement>;
            handleOptionDisplayTextChange(fakeEvent);
        }
    };

    return (
        <Drawer open={open} onClose={onClose} placement="right" title="Edit Option">
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Flex gap="bottom" vertical>
                    <label className="form-label">Source:</label>
                    <Select
                        placeholder="Select source"
                        value={source || undefined}
                        onChange={handleSourceChange}
                        style={{ width: '100%' }}
                    >
                        <Option value="menu">Menu</Option>
                        <Option value="product">Product</Option>
                    </Select>
                </Flex>

                <Flex gap="bottom" vertical>
                    <label className="form-label">Option:</label>
                    <Select
                        showSearch
                        style={{ width: '100%' }}
                        placeholder="Search by name or POS"
                        allowClear
                        value={selectedMenuOptionGuid || undefined}
                        disabled={!source}
                        loading={loadingItems}
                        filterOption={false}
                        onSearch={debouncedSearch}
                        onDropdownVisibleChange={(open) => {
                            if (open && items.length === 0 && source) {
                                fetchLookupItems("");
                            }
                        }}
                        onChange={handleSelectItem}
                    >
                        <Option value="(SELECT)">(SELECT)</Option>
                        {items.map((option) => (
                            <Option key={option.menuGuid} value={option.menuGuid}>
                                {option.name}
                            </Option>
                        ))}
                    </Select>
                    {isWarnMenuOption && (
                        <span style={{ color: 'red' }}>Option is Required</span>
                    )}
                </Flex>

                <Flex gap="bottom" vertical>
                    <label className="form-label">Kiosk Text:</label>
                    <Input
                        value={selectedOptionDisplayName}
                        placeholder="Display Name"
                        onChange={handleOptionDisplayTextChange}
                        style={{
                            borderWidth: '1px',
                            borderStyle: 'solid',
                            borderRadius: '8px 0 0 8px',
                            width: '100%',
                            fontSize: '14px',
                        }}
                    />
                    {isWarnOptionDisplayName && (
                        <span style={{ color: 'red' }}>Kiosk Text is Required</span>
                    )}
                </Flex>

                <Flex gap="bottom" vertical>
                    <label className="form-label">Selected by Default:</label>
                    <Switch
                        style={{ width: '50px' }}
                        checked={selectedOptionDefaultSelection}
                        onChange={handleDefaultOptionChange}
                        checkedChildren="Yes"
                        unCheckedChildren="No"
                    />
                </Flex>
                <Divider />
                <Flex gap="bottom" vertical>
                        <label>Optional dates for seasonal items, or new upcoming items that will be shown for a limited time.  Leave dates blank for options that should be included year round.</label>
                </Flex>
                <Flex gap="bottom" vertical>
                        
                        <label className="form-label">Start Date:</label>
                        <DatePicker
                          showTime={false}
                            format="YYYY-MM-DD"
                            style={{ width: '100%' }}
                            value={selectedOptionStartDate ? dayjs(selectedOptionStartDate) : null}
                            onChange={handleOptionStartDateChange}
                            allowClear={true}
                        />
                </Flex>
                <Flex gap="bottom" vertical>
                        <label className="form-label">End Date:</label>
                    <DatePicker
                    showTime={false}
                    format="YYYY-MM-DD"
                    style={{ width: '100%' }}
                    // Wrap the value in dayjs() if it exists
                    value={selectedOptionEndDate ? dayjs(selectedOptionEndDate) : null}
                        allowClear={true}
                    onChange={handleOptionEndDateChange}

                    />
                </Flex>

                <Flex>
                    <Space direction="horizontal" size="middle">
                        {!isReadOnly && (
                            <Button
                                color="green"
                                type="primary"
                                variant="solid"
                                size="small"
                                style={{
                                    marginTop: '20px',
                                    paddingInline: '15px',
                                    fontSize: '12px',
                                    width: '100px',
                                }}
                                onClick={handleSaveOptionLink}
                            >
                                Save Changes
                            </Button>
                        )}
                        <Button
                            variant="solid"
                            size="small"
                            style={{
                                marginTop: '20px',
                                paddingInline: '15px',
                                fontSize: '12px',
                                width: '100px',
                            }}
                            onClick={handleCancelOptionLink}
                        >
                            Cancel
                        </Button>
                    </Space>
                </Flex>
            </Space>
        </Drawer>
    );
};

export default MenuOptionEditorDrawer;